import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Clock, MessageCircle, Building2, Plane,
  Shield, Users, Award, Star, ChevronDown, CalendarCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';
import { trackEvent } from '@/services/analytics';

/* ─────────────────────────────────────────────────────────────────────
 *  NAP — Single source of truth
 *  Any change here MUST be mirrored in:
 *    • Footer (currently /app/frontend/src/components/layout/Footer.jsx)
 *    • Legal pages (Privacy / Terms / Refund / Disclaimer)
 *    • JSON-LD in index.html if a sitewide Organization schema is added.
 * ─────────────────────────────────────────────────────────────────── */
const PHONE_E164 = '+919958848998';
const PHONE_DISPLAY = '+91-9958848998';
const PHONE_WA = '919958848998'; // wa.me path component
const EMAIL = 'info@zafco.in';
const MEA_RC = 'B-0762/DEL/PER/1000+/5/9148/2015';

const OFFICES = {
  delhi: {
    type: 'Head Office',
    flag: '🇮🇳',
    city: 'New Delhi, India',
    street: 'Ground Floor 5, Building No. 27B, Khizrabad',
    locality: 'New Friends Colony',
    region: 'Delhi',
    postal: '110025',
    country: 'India',
    countryCode: 'IN',
    geo: { lat: 28.5672839, lng: 77.2643873 },
  },
  sharjah: {
    type: 'Overseas Office',
    flag: '🇦🇪',
    city: 'Sharjah, UAE',
    street: 'Shams Free Zone Business Center',
    locality: 'Sharjah Media City',
    region: 'Sharjah',
    postal: '',
    country: 'United Arab Emirates',
    countryCode: 'AE',
    geo: { lat: 25.2914, lng: 55.4747 },
  },
};

/* ─── Recruiter team — placeholder names, swap to actuals before launch ─ */
const RECRUITERS = [
  {
    name: 'Mohd. Asif',
    role: 'Head — Gulf & Saudi Recruitment',
    specialization: 'Saudi Arabia · UAE · Qatar · Construction · Oil & Gas',
    initials: 'MA',
    utm: 'recruiter-asif-gulf',
  },
  {
    name: 'Pooja Sharma',
    role: 'Senior Counsellor — Canada / Australia PR',
    specialization: 'Express Entry · 189 / 190 / 491 · CRS score reviews',
    initials: 'PS',
    utm: 'recruiter-pooja-pr',
  },
  {
    name: 'Rahul Verma',
    role: 'Counsellor — UK / Europe Skilled Worker',
    specialization: 'UK Skilled Worker · Germany Opportunity Card',
    initials: 'RV',
    utm: 'recruiter-rahul-uk-eu',
  },
  {
    name: 'Sneha Iyer',
    role: 'Counsellor — Study Abroad',
    specialization: 'Germany · UK · Canada universities · APS · Visa files',
    initials: 'SI',
    utm: 'recruiter-sneha-study',
  },
];

const FAQS = [
  {
    q: 'Is Zafco HR a Government-approved recruitment agency?',
    a: `Yes. Zafco Human Resource Management is registered with the Ministry of External Affairs, Government of India under RC No. ${MEA_RC}. We are additionally empanelled with the Royal Embassy of Saudi Arabia in New Delhi for direct GAMCA-channel deployments.`,
  },
  {
    q: 'Where is your office located in Delhi?',
    a: `Our Head Office is at ${OFFICES.delhi.street}, ${OFFICES.delhi.locality}, New Delhi – ${OFFICES.delhi.postal}. The nearest metro station is Okhla NSIC (Magenta Line). We also operate from Shams Free Zone, Sharjah Media City, UAE for direct candidate handovers in the Gulf.`,
  },
  {
    q: 'Do you charge candidates a recruitment fee?',
    a: 'For Gulf and Saudi recruitment, all employer-sponsored placements are completely free for the candidate — fees are paid by the hiring company under the e-Migrate framework. Skilled-migration and Study-Abroad consulting carry a transparent professional fee disclosed upfront before any engagement.',
  },
  {
    q: 'How fast will I get a response after I contact you?',
    a: 'WhatsApp messages received during business hours (Mon–Sat 9:00 AM – 6:30 PM IST) are typically answered within 60 seconds. Email enquiries are answered within 24 working hours. Phone lines are staffed throughout office hours.',
  },
  {
    q: 'Can I walk in without an appointment?',
    a: 'Walk-ins are welcome during office hours but candidates with appointments are prioritised. We recommend booking a free 30-minute consultation slot via WhatsApp or the form on this page so the right counsellor is reserved for you.',
  },
  {
    q: 'Which countries do you actively recruit for?',
    a: 'Active monthly deployments: Saudi Arabia, UAE, Qatar, Kuwait, Oman, Bahrain (Gulf cluster); plus skilled-migration filing support for Canada, Australia, UK, Germany and Ireland. Study Abroad active for Germany, UK, Canada, Australia, Ireland and UAE.',
  },
  {
    q: 'Do you provide IELTS / PTE coaching and document help?',
    a: 'Yes. We have in-house IELTS / PTE coaches, certified translation partners (German, French, Spanish), and notarisation desks for police clearance certificates, medicals (GAMCA), and ECA / Skills Assessment files.',
  },
];

const TRUST_TILES = [
  { icon: Shield, label: 'MEA Approved',    value: 'Govt of India' },
  { icon: Users,  label: '10,000+',         value: 'Placements' },
  { icon: Award,  label: '15+ Years',       value: 'Trusted Since 2015' },
  { icon: Star,   label: '4.8 / 5',         value: '482 Verified Reviews' },
];

/* ─── Helpers ─────────────────────────────────────────────────────── */
const buildWhatsAppUrl = (utm, greeting) => {
  const text = greeting || 'Hello Zafco, I would like guidance on overseas opportunities.';
  return (
    `https://wa.me/${PHONE_WA}` +
    `?text=${encodeURIComponent(text)}` +
    `&utm_source=contact-page&utm_medium=whatsapp&utm_campaign=${utm}`
  );
};

const fireWhatsApp = (utm, meta = {}) => {
  trackEvent('whatsapp_click', {
    category: 'lead_generation',
    source: 'contact-page',
    utm_content: utm,
    ...meta,
  });
};

const firePhone = (utm) => {
  trackEvent('phone_click', { category: 'lead_generation', source: 'contact-page', utm_content: utm });
};

/* ─── FAQPage JSON-LD only ─────────────────────────────────────────
 *  LocalBusiness + Organization schemas are now centralised sitewide
 *  in `public/index.html` (single source of truth, avoids Google's
 *  duplicate-entity warnings). Only inject the page-specific FAQPage
 *  schema here.
 * ─────────────────────────────────────────────────────────────── */
const injectSchemas = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const faqEl = document.createElement('script');
  faqEl.type = 'application/ld+json';
  faqEl.setAttribute('data-contact-faq-schema', '1');
  faqEl.text = JSON.stringify(faqSchema)
    .replace(/<\/(script)/gi, '<\\/$1')
    .replace(/<!--/g, '<\\!--');
  document.head.appendChild(faqEl);

  return () => {
    document.head.querySelector('script[data-contact-faq-schema]')?.remove();
  };
};

/* ─── FAQ Accordion ───────────────────────────────────────────────── */
const FAQItem = ({ q, a, open, onToggle, idx }) => (
  <div
    className="bg-slate-900/50 border border-white/5 rounded-2xl overflow-hidden"
    data-testid={`faq-item-${idx}`}
  >
    <button
      type="button"
      onClick={onToggle}
      className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-900/80 transition-colors"
      aria-expanded={open}
    >
      <span className="text-white font-syne font-bold text-sm md:text-base leading-snug">{q}</span>
      <ChevronDown
        className={`w-4 h-4 text-[#C5A028] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        aria-hidden="true"
      />
    </button>
    {open && (
      <div className="px-5 pb-5 text-slate-400 text-sm leading-relaxed">{a}</div>
    )}
  </div>
);

/* ─── Page ────────────────────────────────────────────────────────── */
const ContactPage = () => {
  usePageMeta(
    'Contact Best Overseas Recruitment Agency in Delhi | Zafco HR — MEA Approved',
    'Get in touch with Zafco HR — MEA Approved Overseas Recruitment Agency in Delhi. Call, WhatsApp, or book a free consultation for Gulf jobs, Canada PR & Study Abroad.'
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- all refs are module-level constants
  useEffect(() => injectSchemas(), []);

  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="bg-slate-950" data-testid="contact-page">

      {/* ============ 1. HERO ============ */}
      <PageHeader
        title="Contact Zafco HR — Your Trusted Overseas Recruitment Partner"
        subtitle="Delhi Head Office & Sharjah Overseas Office · Immediate response on WhatsApp during business hours · 10,000+ verified placements since 2015"
        breadcrumbs={[{ label: 'Contact' }]}
        keywordsTier={['MEA Approved', 'Delhi · Sharjah', 'Direct Recruiter Lines']}
      />

      {/* Hero CTA strip — sticks against the PageHeader, above NAP */}
      <section className="bg-slate-900/60 border-y border-white/5" data-testid="hero-cta-strip">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-5">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center justify-center">
            <a
              href={`tel:${PHONE_E164}`}
              onClick={() => firePhone('hero-call-now')}
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm md:text-base px-6 py-3 rounded-full transition-all whitespace-nowrap"
              data-testid="hero-cta-call"
            >
              <Phone className="w-4 h-4" /> Call Now · {PHONE_DISPLAY}
            </a>
            <a
              href={buildWhatsAppUrl('hero-whatsapp')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireWhatsApp('hero-whatsapp')}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-slate-900 font-bold text-sm md:text-base px-6 py-3 rounded-full transition-all whitespace-nowrap"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" fill="white" /> WhatsApp Us
            </a>
            <a
              href="#consultation-form"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white hover:border-[#C5A028] hover:text-[#C5A028] font-semibold text-sm md:text-base px-6 py-3 rounded-full transition-all whitespace-nowrap"
              data-testid="hero-cta-book-consult"
            >
              <CalendarCheck className="w-4 h-4" /> Book Free Consultation
            </a>
          </div>
        </div>
      </section>

      {/* ============ 2. NAP — DUAL OFFICE CARDS ============ */}
      <section className="py-14 md:py-16 bg-slate-950" aria-labelledby="offices-heading" data-testid="offices-section">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-[#C5A028] font-semibold text-xs tracking-widest uppercase mb-2">Two Offices, One Team</p>
            <h2 id="offices-heading" className="text-2xl md:text-3xl font-bold text-white font-syne leading-tight">
              Our Locations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {/* Delhi */}
            <article
              className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 md:p-7 hover:border-[#C5A028]/40 transition-colors"
              data-testid="office-card-delhi"
              itemScope
              itemType="https://schema.org/Place"
            >
              <meta itemProp="name" content="Zafco Human Resource Management — Delhi Head Office" />
              <div className="flex items-start gap-4 mb-5">
                <div className="w-14 h-14 rounded-xl bg-[#C5A028]/15 ring-1 ring-[#C5A028]/30 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-7 h-7 text-[#C5A028]" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#C5A028] font-bold">{OFFICES.delhi.type}</p>
                  <h3 className="text-lg md:text-xl font-bold text-white font-syne flex items-center gap-2 leading-tight">
                    <span aria-hidden="true">{OFFICES.delhi.flag}</span> {OFFICES.delhi.city}
                  </h3>
                </div>
              </div>

              <address
                className="not-italic space-y-3 text-sm"
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0 mt-1" aria-hidden="true" />
                  <p className="text-slate-300 leading-relaxed">
                    <span itemProp="streetAddress">{OFFICES.delhi.street}, {OFFICES.delhi.locality}</span>,<br />
                    <span itemProp="addressLocality">New Delhi</span> – <span itemProp="postalCode">{OFFICES.delhi.postal}</span>
                    <meta itemProp="addressRegion" content={OFFICES.delhi.region} />
                    <meta itemProp="addressCountry" content={OFFICES.delhi.countryCode} />
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden="true" />
                  <a
                    href={`tel:${PHONE_E164}`}
                    onClick={() => firePhone('delhi-card-call')}
                    className="text-white font-semibold hover:text-[#C5A028] transition-colors"
                    data-testid="office-delhi-phone"
                    itemProp="telephone"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden="true" />
                  <a
                    href={`mailto:${EMAIL}`}
                    className="text-white font-semibold hover:text-[#C5A028] transition-colors"
                    itemProp="email"
                  >
                    {EMAIL}
                  </a>
                </div>
              </address>

              <meta itemProp="url" content="https://zafco.in/contact/" />
              <link itemProp="hasMap" href={`https://www.google.com/maps/search/?api=1&query=${OFFICES.delhi.geo.lat},${OFFICES.delhi.geo.lng}`} />

              {/* Trust signal — visual only.
                  IMPORTANT: AggregateRating microdata removed (Feb 2026) to resolve the
                  "Review has multiple aggregate ratings" Google Search Console warning.
                  The canonical rating lives once on LocalBusiness/EmploymentAgency in the
                  sitewide @graph (public/index.html). Keeping inline microdata duplicated
                  it on the same entity — confusing Google's parser and risking a
                  star-snippet suppression. */}
              <div
                className="mt-4 flex items-center gap-2 text-xs text-slate-400"
                data-testid="office-delhi-rating-trust"
              >
                <Star className="w-3.5 h-3.5 text-[#C5A028] fill-[#C5A028]" aria-hidden="true" />
                <p className="leading-tight">
                  Rated <span className="text-white font-semibold">4.8</span>/5 based on{' '}
                  <span className="text-white font-semibold">482</span> Google reviews
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-white/5 text-[10px] font-mono text-[#C5A028]">
                RC: {MEA_RC}
              </div>
            </article>

            {/* Sharjah */}
            <article
              className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 md:p-7 hover:border-[#C5A028]/40 transition-colors"
              data-testid="office-card-sharjah"
              itemScope
              itemType="https://schema.org/Place"
            >
              <meta itemProp="name" content="Zafco Human Resource Management — Sharjah Overseas Office" />
              <div className="flex items-start gap-4 mb-5">
                <div className="w-14 h-14 rounded-xl bg-[#C5A028]/15 ring-1 ring-[#C5A028]/30 flex items-center justify-center flex-shrink-0">
                  <Plane className="w-7 h-7 text-[#C5A028]" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#C5A028] font-bold">{OFFICES.sharjah.type}</p>
                  <h3 className="text-lg md:text-xl font-bold text-white font-syne flex items-center gap-2 leading-tight">
                    <span aria-hidden="true">{OFFICES.sharjah.flag}</span> {OFFICES.sharjah.city}
                  </h3>
                </div>
              </div>

              <address
                className="not-italic space-y-3 text-sm"
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0 mt-1" aria-hidden="true" />
                  <p className="text-slate-300 leading-relaxed">
                    <span itemProp="streetAddress">{OFFICES.sharjah.street}, {OFFICES.sharjah.locality}</span>,<br />
                    <span itemProp="addressLocality">{OFFICES.sharjah.region}</span>,<br />
                    <span itemProp="addressCountry">{OFFICES.sharjah.country}</span>
                    <meta itemProp="addressRegion" content={OFFICES.sharjah.region} />
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarCheck className="w-4 h-4 text-slate-500 flex-shrink-0 mt-1" aria-hidden="true" />
                  <p className="text-slate-400 text-xs leading-relaxed">
                    On-ground handovers for candidates deploying to Gulf states. By appointment only — coordinated via the Delhi desk.
                  </p>
                </div>
              </address>

              <meta itemProp="telephone" content={PHONE_E164} />
              <meta itemProp="email" content={EMAIL} />
              <meta itemProp="url" content="https://zafco.in/contact/" />
              <link itemProp="hasMap" href={`https://www.google.com/maps/search/?api=1&query=${OFFICES.sharjah.geo.lat},${OFFICES.sharjah.geo.lng}`} />

              {/* Trust signal — visual only (microdata intentionally absent; see Delhi card). */}
              <div
                className="mt-4 flex items-center gap-2 text-xs text-slate-400"
                data-testid="office-sharjah-rating-trust"
              >
                <Star className="w-3.5 h-3.5 text-[#C5A028] fill-[#C5A028]" aria-hidden="true" />
                <p className="leading-tight">
                  Rated <span className="text-white font-semibold">4.8</span>/5 based on{' '}
                  <span className="text-white font-semibold">482</span> Google reviews
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-white/5">
                <a
                  href={buildWhatsAppUrl('sharjah-coordinate', 'Hello Zafco, I need to coordinate with the Sharjah office.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => fireWhatsApp('sharjah-coordinate', { office: 'sharjah' })}
                  className="inline-flex items-center gap-2 text-[#C5A028] hover:text-[#EAB308] text-sm font-semibold transition-colors"
                  data-testid="office-sharjah-coordinate-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Coordinate via Delhi WhatsApp →
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ============ 3. GOOGLE MAP ============ */}
      <section className="pb-14 md:pb-16 bg-slate-950" aria-labelledby="map-heading" data-testid="map-section">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-6">
            <h2 id="map-heading" className="text-2xl md:text-3xl font-bold text-white font-syne mb-2 leading-tight">
              Visit the Delhi Head Office
            </h2>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${OFFICES.delhi.geo.lat},${OFFICES.delhi.geo.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#C5A028] text-sm font-semibold hover:underline"
              data-testid="map-directions-link"
            >
              Get Directions on Google Maps →
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.9891427631706!2d77.26438731508035!3d28.5672839824381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce35a44444441%3A0x2bc234578edc9f9!2sNew%20Friends%20Colony%2C%20New%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1635678901234!5m2!1sen!2sin"
              width="100%"
              height="380"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Zafco HR Delhi Head Office — Google Map"
            />
          </div>
        </div>
      </section>

      {/* ============ 4. CONTACT OPTIONS + FORM ============ */}
      <section
        className="py-16 md:py-20 bg-slate-900/30 border-y border-white/5"
        id="consultation-form"
        aria-labelledby="ways-to-connect"
        data-testid="ways-section"
      >
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#C5A028] font-semibold text-xs tracking-widest uppercase mb-2">Pick Your Channel</p>
            <h2 id="ways-to-connect" className="text-2xl md:text-3xl font-bold text-white font-syne leading-tight">
              Multiple Ways to Reach Us
            </h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Channel grid */}
            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
              <a
                href={`tel:${PHONE_E164}`}
                onClick={() => firePhone('channel-direct-call')}
                className="group bg-slate-900/60 border border-white/10 hover:border-[#C5A028]/40 rounded-2xl p-6 transition-all"
                data-testid="channel-phone"
              >
                <Phone className="w-7 h-7 text-[#C5A028] mb-4" aria-hidden="true" />
                <h3 className="text-base font-bold text-white font-syne mb-1">Direct Phone</h3>
                <p className="text-slate-400 text-sm">{PHONE_DISPLAY}</p>
                <p className="text-[11px] text-slate-400 mt-1">Mon–Sat · 9 AM – 6:30 PM IST</p>
              </a>

              <a
                href={buildWhatsAppUrl('channel-whatsapp')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => fireWhatsApp('channel-whatsapp')}
                className="group bg-slate-900/60 border border-white/10 hover:border-[#25D366]/50 rounded-2xl p-6 transition-all"
                data-testid="channel-whatsapp"
              >
                <MessageCircle className="w-7 h-7 text-[#25D366] mb-4" fill="#25D366" aria-hidden="true" />
                <h3 className="text-base font-bold text-white font-syne mb-1">WhatsApp Chat</h3>
                <p className="text-slate-400 text-sm">Respond in 60 sec</p>
                <p className="text-[11px] text-slate-400 mt-1">UTM-tracked for analytics</p>
              </a>

              <a
                href={`mailto:${EMAIL}`}
                className="group bg-slate-900/60 border border-white/10 hover:border-[#C5A028]/40 rounded-2xl p-6 transition-all"
                data-testid="channel-email"
              >
                <Mail className="w-7 h-7 text-[#C5A028] mb-4" aria-hidden="true" />
                <h3 className="text-base font-bold text-white font-syne mb-1">Email</h3>
                <p className="text-slate-400 text-sm">{EMAIL}</p>
                <p className="text-[11px] text-slate-400 mt-1">Reply within 24 hrs</p>
              </a>

              <Link
                to="/study-abroad/book-counselling/?source=contact-channel-booking"
                className="group bg-gradient-to-br from-[#C5A028]/15 to-slate-900 border border-[#C5A028]/40 hover:border-[#C5A028] rounded-2xl p-6 transition-all"
                data-testid="channel-book"
              >
                <CalendarCheck className="w-7 h-7 text-[#C5A028] mb-4" aria-hidden="true" />
                <h3 className="text-base font-bold text-white font-syne mb-1">Book a 30-min Slot</h3>
                <p className="text-slate-400 text-sm">Pick your time</p>
                <p className="text-[11px] text-slate-400 mt-1">Free consultation</p>
              </Link>
            </div>

            {/* Inline form — anchor target */}
            <div className="lg:col-span-2">
              <div className="glass-premium rounded-2xl p-6 md:p-7" data-testid="contact-form-card">
                <h3 className="text-xl md:text-2xl font-bold text-white font-syne mb-2 leading-tight">
                  Or send a written enquiry
                </h3>
                <p className="text-slate-400 text-sm mb-5">
                  Fill the form and the right counsellor will reach out within 24 working hours.
                </p>
                <ConsultationForm variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 5. RECRUITER TEAM ============ */}
      <section className="py-16 md:py-20 bg-slate-950" aria-labelledby="team-heading" data-testid="team-section">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-[#C5A028] font-semibold text-xs tracking-widest uppercase mb-2">Talk to a Specialist Directly</p>
            <h2 id="team-heading" className="text-2xl md:text-3xl font-bold text-white font-syne mb-3 leading-tight">
              Meet the Counsellors
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm leading-relaxed">
              Skip the front desk — message the counsellor handling your destination. Each WhatsApp line is UTM-tracked so we know who handed off the lead.
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="recruiter-list">
            {RECRUITERS.map((r) => (
              <li
                key={r.name}
                className="bg-slate-900/50 border border-white/5 hover:border-[#C5A028]/30 rounded-2xl p-5 flex flex-col transition-all"
                data-testid={`recruiter-card-${r.utm}`}
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A028]/25 to-[#C5A028]/5 ring-1 ring-[#C5A028]/30 flex items-center justify-center mx-auto mb-3 font-syne text-lg font-bold text-[#C5A028]">
                  {r.initials}
                </div>
                <h3 className="text-white font-bold font-syne text-base text-center leading-tight">{r.name}</h3>
                <p className="text-[#C5A028] text-[11px] font-semibold uppercase tracking-wider text-center mt-1 leading-tight">{r.role}</p>
                <p className="text-slate-400 text-xs text-center mt-2 leading-snug flex-1">{r.specialization}</p>
                <a
                  href={buildWhatsAppUrl(r.utm, `Hello ${r.name.split(' ')[0]}, I would like guidance on overseas opportunities.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => fireWhatsApp(r.utm, { recruiter: r.name, role: r.role })}
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-slate-900 font-bold text-sm px-4 py-2.5 rounded-full transition-colors"
                  data-testid={`recruiter-whatsapp-${r.utm}`}
                >
                  <MessageCircle className="w-4 h-4" fill="white" /> WhatsApp
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 6. BUSINESS HOURS ============ */}
      <section className="py-12 md:py-14 bg-slate-900/30 border-y border-white/5" data-testid="hours-section">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-6 md:p-7 max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-5">
              <Clock className="w-7 h-7 text-[#C5A028]" aria-hidden="true" />
              <h2 className="text-xl md:text-2xl font-bold text-white font-syne leading-tight">Business Hours</h2>
            </div>
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-950/40 border border-white/5">
                <dt className="text-slate-400">Monday – Saturday</dt>
                <dd className="text-white font-bold">9:00 AM – 6:30 PM IST</dd>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-950/40 border border-white/5">
                <dt className="text-slate-400">Sunday</dt>
                <dd className="text-slate-300 font-semibold">Closed · By Appointment</dd>
              </div>
            </dl>
            <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
              All times are India Standard Time (IST). For urgent visa enquiries outside hours, drop a WhatsApp message and we will reply first thing the next working morning.
            </p>
          </div>
        </div>
      </section>

      {/* ============ 7. TRUST STRIP ============ */}
      <section className="bg-slate-950 py-10 border-b border-white/5" data-testid="contact-trust-strip">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {TRUST_TILES.map((t) => (
              <li
                key={t.label}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-900/40 border border-white/5"
                data-testid={`trust-${t.label.toLowerCase().replace(/[+/\s]+/g, '-')}`}
              >
                <t.icon className="w-5 h-5 text-[#C5A028] flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold text-white leading-tight">{t.label}</p>
                  <p className="text-[11px] text-slate-400 leading-tight">{t.value}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 8. FAQ ============ */}
      <section className="py-16 md:py-20 bg-slate-950" aria-labelledby="faq-heading" data-testid="faq-section">
        <div className="container max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="text-[#C5A028] font-semibold text-xs tracking-widest uppercase mb-2">FAQ</p>
            <h2 id="faq-heading" className="text-2xl md:text-3xl font-bold text-white font-syne leading-tight">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FAQItem
                key={f.q}
                idx={i}
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ 9. FINAL CTA ============ */}
      <section
        className="relative py-16 md:py-20 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border-t border-white/5 overflow-hidden"
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C5A028] rounded-full blur-[160px]" />
        </div>
        <div className="container max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center relative">
          <h2 className="text-2xl md:text-4xl font-bold text-white font-syne mb-4 leading-tight">
            Ready to start your overseas journey?
          </h2>
          <p className="text-slate-400 mb-8 leading-relaxed max-w-xl mx-auto">
            One free, no-obligation 30-minute consultation. We'll review your profile, recommend the
            fastest pathway, and tell you honestly if you're not yet ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={buildWhatsAppUrl('final-cta-whatsapp')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireWhatsApp('final-cta-whatsapp')}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-slate-900 font-bold text-base px-7 py-3.5 rounded-full transition-colors"
              data-testid="final-cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5" fill="white" /> WhatsApp Now
            </a>
            <Button
              asChild
              size="lg"
              className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-base px-7 py-6 rounded-full"
              data-testid="final-cta-form"
            >
              <a href="#consultation-form">
                <CalendarCheck className="w-5 h-5 mr-2" /> Send Enquiry Form
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
